/**
 * JobRegisterTab — used inside the Job modal. Pick Equipment or Electrical
 * items from master inventory (or add a brand-new one inline) and attach to
 * the job. Shows test-tag due-state badges for electrical items.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Box, Zap, Trash2, AlertTriangle } from "lucide-react";
import { Button, Card, Badge, Spinner, Input, Modal, Select } from "../shared/ui";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dueBadge = (due) => {
  if (!due || due.state === "unknown") return null;
  if (due.state === "expired") return <Badge variant="danger">OVERDUE</Badge>;
  if (due.state === "warning") return <Badge variant="warning">DUE SOON</Badge>;
  return <Badge variant="success">OK</Badge>;
};

const JobRegisterTab = ({ jobId, kind /* 'equipment' | 'electrical' */ }) => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pickerId, setPickerId] = useState("");
  const [search, setSearch] = useState("");
  const [newItem, setNewItem] = useState({});
  const [creatingNew, setCreatingNew] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/register/${kind}`),
        axios.get(`${API}/inventory/${kind}`),
      ]);
      setItems(a.data.items);
      setSummary(a.data.summary || {});
      setInventory(b.data.items);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (jobId) load(); /* eslint-disable-next-line */ }, [jobId, kind]);

  const handleAdd = async () => {
    try {
      const payload = creatingNew
        ? { new_item: newItem, quantity, notes }
        : { inventory_item_id: pickerId, quantity, notes };
      await axios.post(`${API}/jobs/${jobId}/register/${kind}`, payload);
      toast.success("Added");
      setShowAdd(false);
      setNewItem({});
      setPickerId("");
      setQuantity(1);
      setNotes("");
      setCreatingNew(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const handleRemove = async (rowId) => {
    if (!window.confirm("Remove this item from the job?")) return;
    await axios.delete(`${API}/jobs/${jobId}/register/${kind}/${rowId}`);
    toast.success("Removed");
    load();
  };

  const filtered = inventory.filter((i) =>
    !search ||
    (i.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (i.serial_number || "").toLowerCase().includes(search.toLowerCase())
  );

  const Icon = kind === "electrical" ? Zap : Box;

  return (
    <div className="space-y-3" data-testid={`job-register-${kind}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${kind === "electrical" ? "text-yellow-500" : "text-green-600"}`} />
          <p className="font-bold uppercase text-sm">{kind === "electrical" ? "Electrical Register" : "Equipment Register"}</p>
          <Badge>{summary.total || 0}</Badge>
          {summary.expired > 0 && <Badge variant="danger">{summary.expired} OVERDUE</Badge>}
          {summary.warning > 0 && <Badge variant="warning">{summary.warning} DUE SOON</Badge>}
        </div>
        <Button size="sm" onClick={() => setShowAdd(true)} data-testid={`job-register-add-${kind}`}>
          <Plus className="w-4 h-4 mr-1" /> Add Item
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Spinner /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-slate-500 py-6">No items in this register yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <Card key={r.id} className="p-3" data-testid={`job-register-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold">{r.name || "Unnamed"}</p>
                    {r.serial_number && <span className="text-xs text-slate-500">#{r.serial_number}</span>}
                    {r.quantity > 1 && <Badge>× {r.quantity}</Badge>}
                    {dueBadge(r.due)}
                  </div>
                  {kind === "electrical" && (
                    <p className="text-xs text-slate-600 mt-1">
                      Last tag: {r.last_tag_test_date || "—"} · Next: <strong>{r.next_tag_test_date || "—"}</strong>
                    </p>
                  )}
                  {r.notes && <p className="text-xs text-slate-500 italic mt-1">{r.notes}</p>}
                  <p className="text-[10px] text-slate-400 mt-1">Added by {r.added_by_name} · {new Date(r.added_at).toLocaleDateString("en-AU")}</p>
                </div>
                <button onClick={() => handleRemove(r.id)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`job-register-del-${r.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showAdd && (
        <Modal isOpen={showAdd} onClose={() => setShowAdd(false)} title={`Add ${kind} to Job`}>
          <div className="space-y-3">
            {!creatingNew ? (
              <>
                <Input placeholder="Search master inventory..." value={search} onChange={(e) => setSearch(e.target.value)} testId="inv-search" />
                <div className="max-h-60 overflow-y-auto border-2 border-slate-200 rounded">
                  {filtered.length === 0 && (
                    <p className="text-center text-slate-500 py-4 text-sm">No items found. Use "Add new item" below.</p>
                  )}
                  {filtered.map((i) => (
                    <button
                      key={i.id}
                      onClick={() => setPickerId(i.id)}
                      className={`w-full flex items-center justify-between text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 ${pickerId === i.id ? "bg-orange-50" : ""}`}
                      data-testid={`inv-item-${i.id}`}
                    >
                      <span>
                        <strong className="text-sm">{i.name}</strong>
                        {i.serial_number && <span className="text-xs text-slate-500 ml-2">#{i.serial_number}</span>}
                      </span>
                      {dueBadge(i.due)}
                    </button>
                  ))}
                </div>
                <button onClick={() => { setCreatingNew(true); setPickerId(""); }} className="text-orange-500 text-sm font-bold hover:underline">+ Add a brand-new item</button>
              </>
            ) : (
              <>
                <Input label="Name *" value={newItem.name || ""} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} />
                <Input label="Serial Number" value={newItem.serial_number || ""} onChange={(e) => setNewItem({ ...newItem, serial_number: e.target.value })} />
                <Input label="Category" value={newItem.category || ""} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })} placeholder={kind === "electrical" ? "e.g. power_board, extension_lead" : "e.g. harness, hand_tool"} />
                {kind === "electrical" && (
                  <Input label="Last Tag Test Date" type="date" value={newItem.last_tag_test_date || ""} onChange={(e) => setNewItem({ ...newItem, last_tag_test_date: e.target.value })} />
                )}
                <button onClick={() => setCreatingNew(false)} className="text-blue-500 text-sm hover:underline">← back to picker</button>
              </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Input label="Quantity" type="number" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} />
            </div>
            <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

            <div className="flex gap-3 pt-2">
              <Button onClick={handleAdd} disabled={!creatingNew && !pickerId} data-testid="job-register-save-btn">Add to Job</Button>
              <Button variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default JobRegisterTab;
